import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Spinner, Breadcrumb } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEye, faTrash, faClock, faDollarSign, faUsers, faDownload } from '@fortawesome/free-solid-svg-icons';
import { getSavedBudgets, deleteBudget, downloadBudgetById } from '../../../service/workflow-api';
import './BudgetPlanner.css';

const SavedBudgets = ({ onBack }) => {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const downloadBudget = async (id) => {
    try {
        const response = await downloadBudgetById(id); // Assuming this fetches the file from the server.

        // Check the response status before proceeding.
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Create a link element and trigger download
        const blob = await response.blob(); // Use blob() to get the response as a Blob
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `budget-${new Date().toString()}.xlsx`); // Use template literals correctly
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Error downloading budget:", error);
        // Handle the error appropriately, e.g., display a message to the user.
        alert("Failed to download the budget. Please try again later."); // Example user feedback
    }
};
  

  useEffect(() => {
    loadSavedBudgets();
  }, []);

  const loadSavedBudgets = async () => {
    try {
      const data = await getSavedBudgets();
      setBudgets(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load saved budgets');
      setLoading(false);
    }
  };

  const handleDelete = async (budgetId) => {
    if (window.confirm('Are you sure you want to delete this budget?')) {
      try {
        await deleteBudget(budgetId);
        setBudgets(budgets.filter(budget => budget.id !== budgetId));
      } catch (err) {
        setError('Failed to delete budget');
      }
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <>
      <Breadcrumb className="dark-breadcrumb mb-4">
        <Breadcrumb.Item href="/ai-workflows">AI Workflows</Breadcrumb.Item>
        <Breadcrumb.Item href="#" onClick={onBack}>Budget Planner</Breadcrumb.Item>
        <Breadcrumb.Item active>Saved Budgets</Breadcrumb.Item>
      </Breadcrumb>

      <Container fluid className="py-4">
        <Card className="dark-main-card">
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="mb-0 text-white" style={{ fontSize: "1.5rem" }}>Saved Budgets</h2>
              <Button 
                variant="outline-primary" 
                className="dark-outline-button"
                onClick={onBack}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Budget Planner
              </Button>
            </div>

            {error && (
              <div className="alert dark-alert alert-danger mb-4">
                {error}
              </div>
            )}

            {budgets.length === 0 ? (
              <Card className="dark-card-alt text-center p-5">
                <Card.Text className="text-muted">
                  No saved budgets found. Create a new budget to get started.
                </Card.Text>
              </Card>
            ) : (
              <div className="table-responsive">
                <Table className="table-dark">
                  <thead>
                    <tr>
                      <th>Event Name</th>
                      <th>Type</th>
                      <th>Total Budget</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {budgets.map((budget) => (
                      <tr key={budget.id}>
                        <td className="text-white">{budget.event_name}</td>
                        <td>
                          <Badge bg="primary" className="dark-badge">
                            {budget.event_type}
                          </Badge>
                        </td>
                        <td>
                          <FontAwesomeIcon icon={faDollarSign} className="me-1 text-primary" />
                          {budget.total_budget}
                        </td>
                        <td>
                          <FontAwesomeIcon icon={faClock} className="me-1 text-muted" />
                          {new Date(budget.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <Button 
                            variant="link" 
                            className="text-primary me-2 p-0"
                            onClick={() => downloadBudget(budget.id)}
                          >
                            <FontAwesomeIcon icon={faDownload} />
                          </Button>
                          <Button 
                            variant="link" 
                            className="text-danger p-0"
                            onClick={() => handleDelete(budget.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default SavedBudgets; 
